import Overlay, { OverlayTransparency } from "./Overlay";
import type { Options } from "@popperjs/core";
import classNames from "classnames";
import isEqual from "lodash/isEqual";
import {
  CSSProperties,
  DetailedHTMLProps,
  Dispatch,
  FC,
  HTMLAttributes,
  ReactNode,
  SetStateAction,
  createElement,
  memo,
  useRef,
  useState,
} from "react";
import { usePopper } from "react-popper";
import useOutsideHandler from "~@/hooks/useOutsideHandler";

export type DropdownProps = {
  isDropdownOpen: boolean;
  onRequestClose?: () => void;
  dropdownClassName?: string;
  hasOverlay?: boolean;
  overlayTransparency?: OverlayTransparency;
  popperOptions?: Partial<Options>;
  children: (props: {
    menuReference: Dispatch<SetStateAction<HTMLElement | null>>;
    styles: { [key: string]: CSSProperties };
    attributes: { [key: string]: { [key: string]: string } | undefined };
  }) => ReactNode;
  rootElement?: keyof JSX.IntrinsicElements;
} & Omit<
  DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
  "className" | "ref" | "children"
>;

const Dropdown: FC<DropdownProps> = ({
  isDropdownOpen,
  onRequestClose,
  dropdownClassName,
  hasOverlay = true,
  overlayTransparency = OverlayTransparency.Ten,
  popperOptions,
  children,
  rootElement = "div",
  ...props
}) => {
  const dropdownRef = useRef<HTMLElement | null>(null);
  const [triggerRef, setTriggerRef] = useState<HTMLElement | null>(null);
  const [menuRef, setMenuRef] = useState<HTMLElement | null>(null);

  const { styles, attributes } = usePopper(triggerRef, menuRef, popperOptions);

  useOutsideHandler(
    dropdownRef,
    typeof onRequestClose === "function" ? onRequestClose : () => {}
  );

  return createElement(
    rootElement,
    {
      className: classNames(
        "dropdown",
        {
          "dropdown--has-overlay": hasOverlay,
          "dropdown--is-active": isDropdownOpen,
        },
        dropdownClassName
      ),
      ref: (node: HTMLElement) => {
        setTriggerRef(node);
        dropdownRef.current = node;
      },
      ...props,
    },
    <>
      {children({
        menuReference: setMenuRef,
        styles,
        attributes,
      })}
      {hasOverlay && (
        <Overlay
          showOverlay={isDropdownOpen}
          overlayTransparency={overlayTransparency}
        />
      )}
    </>
  );
};

export default memo(Dropdown, isEqual);
